import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { brandAuthService } from "../../../services/brand-auth.service";
import { clearOnboardingSkip } from "@/lib/brandOnboarding";
import { FormData } from "./components/PersonalDetails";
import toast from "react-hot-toast";
import { countries } from "../../../constants/countries";
import { nicheService } from "../../../services/niche.service";
import { planService } from "../../../services/planService";
import { ChevronDown, EyeOff, Eye, X, Check } from "react-feather";
import { ImageUploader } from "../../../components/ImageUploader/ImageUploader";
import { storageService } from "../../../services/storage.service";
import { useSignupPreview } from "../../../contexts/SignupPreviewContext";
import {
  AttributionMethodSelector,
  AttributionMethod,
} from "../../../components/sneakylink";

type Step = 1 | 2 | 3;

interface Niche {
  id: string;
  name: string;
  description: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  nicheId: string | null;
}

interface ProgramSettings {
  commissionRate: string;
  discountPercentage: string;
  autoApproveAffiliates: boolean;
  attributionMethod: "STATIC_CODE" | "SNEAKYLINK";
}

export const BrandSignUp = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const preFilledData = location.state || {};
  const {
    setSignupStep,
    setFormData: setPreviewFormData,
    formData: previewFormData,
  } = useSignupPreview();
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAdminInvite, setIsAdminInvite] = useState(false);

  // Step 1 states
  const [showPassword, setShowPassword] = useState(false);

  // Step 2 states
  const [availableNiches, setAvailableNiches] = useState<Niche[]>([]);
  const [isLoadingNiches, setIsLoadingNiches] = useState(false);
  const [isNicheOpen, setIsNicheOpen] = useState(false);
  const [nicheQuery, setNicheQuery] = useState("");
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string>("");
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  // Step 3 states
  const [programSettings, setProgramSettings] = useState<ProgramSettings>({
    commissionRate: "10",
    discountPercentage: "10",
    autoApproveAffiliates: false,
    attributionMethod: "STATIC_CODE",
  });

  // Validation states
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Detect and validate admin invite from localStorage token + backend verification
  useEffect(() => {
    const checkAdminInvite = async () => {
      try {
        const storedToken = localStorage.getItem("rs_admin_invite_token");
        if (!storedToken) {
          setIsAdminInvite(false);
          return;
        }

        // Attach this token to the current session on the backend.
        // Backend will verify the invite is valid, unused, and matches this shop.
        await planService.attachAdminInviteToken(storedToken);
        setIsAdminInvite(true);
      } catch {
        // Token is not valid/active for this session anymore; clean it up
        setIsAdminInvite(false);
        localStorage.removeItem("rs_admin_invite_token");
      }
    };

    checkAdminInvite();
  }, []);

  // Initialize form data with onboarding data if available
  const [formData, setFormData] = useState<FormData>(() => {
    if (preFilledData.onboardingData?.data) {
      const { data } = preFilledData.onboardingData;
      return {
        firstName: data?.firstName || preFilledData.firstName || "",
        lastName: data?.lastName || preFilledData.lastName || "",
        email: data?.email || preFilledData.email || "",
        password: "",
        confirmPassword: "",
        country: data?.country || preFilledData.country || "United states",
        companyName: data?.companyName || preFilledData.companyName || "",
        website:
          data?.website ||
          (preFilledData.shopifyDomain
            ? `https://${preFilledData.shopifyDomain}`
            : ""),
        niches: [],
        description: data?.description || "",
        shopifyDomain: preFilledData.shopifyDomain || "",
        agreeToTerms: false,
      };
    }

    return {
      firstName: preFilledData.firstName || "",
      lastName: preFilledData.lastName || "",
      email: preFilledData.email || "",
      password: "",
      confirmPassword: "",
      country: preFilledData.country || "United states",
      companyName: preFilledData.companyName || "",
      website: preFilledData.shopifyDomain
        ? `https://${preFilledData.shopifyDomain}`
        : "",
      niches: [],
      description: preFilledData.onboardingData?.description || "",
      shopifyDomain: preFilledData.shopifyDomain || "",
      agreeToTerms: false,
    };
  });

  // Sync companyName with firstName (only if companyName is empty)
  useEffect(() => {
    setFormData((prev) => {
      if (prev.firstName && !prev.companyName) {
        return { ...prev, companyName: prev.firstName };
      }
      return prev;
    });
  }, [formData.firstName]);

  // Fetch niches
  useEffect(() => {
    const fetchNiches = async () => {
      setIsLoadingNiches(true);
      try {
        const niches = await nicheService.getAvailableNiches();
        setAvailableNiches(niches);
      } catch (error) {
        console.error("Failed to fetch niches:", error);
      } finally {
        setIsLoadingNiches(false);
      }
    };
    fetchNiches();
  }, []);

  // Sync current step to context
  useEffect(() => {
    setSignupStep(currentStep);
  }, [currentStep, setSignupStep]);

  // Sync form data to context for preview
  useEffect(() => {
    const previewData = {
      companyName: formData.companyName,
      brandProfileImage: formData.brandProfileImage || logoPreviewUrl,
      niches: formData.niches
        .map((nicheId) => {
          const niche = availableNiches.find((n) => n.id === nicheId);
          return niche ? { id: niche.id, name: niche.name } : null;
        })
        .filter(Boolean) as Array<{ id: string; name: string }>,
      // Preserve programSettings if they exist
      programSettings: previewFormData.programSettings,
    };
    setPreviewFormData(previewData);
  }, [
    formData.companyName,
    formData.brandProfileImage,
    formData.niches,
    logoPreviewUrl,
    availableNiches,
    setPreviewFormData,
    previewFormData.programSettings,
  ]);

  // Sync program settings to context for step 3 preview
  useEffect(() => {
    if (currentStep === 3) {
      setPreviewFormData({
        ...previewFormData,
        programSettings: {
          commissionRate: programSettings.commissionRate,
          discountPercentage: programSettings.discountPercentage,
        },
      });
    }
  }, [
    currentStep,
    programSettings.commissionRate,
    programSettings.discountPercentage,
    setPreviewFormData,
    previewFormData,
  ]);

  const validateStep1 = () => {
    const errors: Record<string, string> = {};

    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    } else if (!/(?=.*[a-z])/.test(formData.password)) {
      errors.password = "Password must contain at least one lowercase letter";
    } else if (!/(?=.*[A-Z])/.test(formData.password)) {
      errors.password = "Password must contain at least one uppercase letter";
    } else if (!/(?=.*\d)/.test(formData.password)) {
      errors.password = "Password must contain at least one number";
    } else if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?/~`]/.test(formData.password)) {
      errors.password = "Password must contain at least one special character";
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (formData.confirmPassword !== formData.password) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (!formData.agreeToTerms) {
      errors.agreeToTerms = "You must agree to the terms and conditions";
    }

    return errors;
  };

  const validateStep2 = () => {
    const errors: Record<string, string> = {};

    if (formData.niches.length === 0) {
      errors.niches = "Please select at least one niche";
    }

    return errors;
  };

  const handleNext = () => {
    let errors: Record<string, string> = {};

    if (currentStep === 1) {
      errors = validateStep1();
      setTouched({
        password: true,
        confirmPassword: true,
        agreeToTerms: true,
      });
    } else if (currentStep === 2) {
      errors = validateStep2();
      setTouched((prev) => ({ ...prev, niches: true }));
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length === 0) {
      if (currentStep < 3) {
        setCurrentStep((currentStep + 1) as Step);
      }
    }
  };

  const handleLogoUpload = async (file: File) => {
    try {
      setIsUploadingLogo(true);
      const url = await storageService.uploadCompanyLogo(file);
      setLogoPreviewUrl(url);
      setFormData((prev) => ({ ...prev, brandProfileImage: url }));
    } catch (e) {
      // no-op
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleSubmit = async (skipStep3 = false) => {
    setError(null);
    setIsLoading(true);

    const token = localStorage.getItem("brand_temp_token");
    localStorage.setItem("brand_token", token || "");

    if (!token) {
      setError("Authentication token not found");
      setIsLoading(false);
      return;
    }

    try {
      // Submit personal details (Step 1 data)
      // No generated letter avatar: it bakes in a fixed colour that clashes
      // once the dashboard wears the brand's palette. The sidebar shows a
      // neutral icon until the user uploads a photo.
      const profileImage = "";

      const personalData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        country: formData.country,
        profileImage,
      };

      const response = await brandAuthService.registerPersonalDetails(
        personalData,
        token
      );

      if (response.success) {
        toast.success("Personal details submitted successfully");
      }

      // Submit company details (Step 2) and optionally program settings (Step 3) together
      const companyData: any = {
        // Step 2: Company details (always required)
        companyName: formData.companyName,
        website: formData.website,
        niches: formData.niches,
        description: formData.description,
        brandProfileImage: formData.brandProfileImage,
        brandBannerImage: formData.brandBannerImage,
        brandBannerImageMobile: formData.brandBannerImageMobile,
        brandBannerImageOriginal: formData.brandBannerImageOriginal,
      };

      // Step 3: Program settings (optional - only if not skipped)
      if (!skipStep3) {
        companyData.defaultCommissionRate =
          parseFloat(programSettings.commissionRate) || 10;
        companyData.defaultDiscountPercentage =
          parseFloat(programSettings.discountPercentage) || 10;
        companyData.autoApproveAffiliates =
          programSettings.autoApproveAffiliates;
        companyData.attributionMethod = programSettings.attributionMethod;
      }

      // Attach landing free trial indicator from cookie if present and unexpired
      try {
        const cookieMatch = document.cookie
          .split(";")
          .map((c) => c.trim())
          .find((c) => c.startsWith("rs_landing_trial="));
        if (cookieMatch) {
          const raw = decodeURIComponent(cookieMatch.split("=")[1] || "");
          const parsed = JSON.parse(raw);
          if (
            parsed?.value === "LANDING" &&
            typeof parsed?.expiresAt === "number" &&
            parsed.expiresAt > Date.now()
          ) {
            companyData.landingFreeTrial = true;
          }
        }
      } catch {}

      await brandAuthService.registerCompanyDetails(companyData, token);

      // Ensure brand_token is definitely set before proceeding
      if (!localStorage.getItem("brand_token")) {
        localStorage.setItem("brand_token", token);
      }

      localStorage.removeItem("brand_temp_token");
      // Clear the landing trial cookie after successful completion
      try {
        const domain = window.location.hostname.replace(/^www\./, "");
        document.cookie = `rs_landing_trial=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; Domain=${domain}; SameSite=Lax;`;
      } catch {}

      toast.success("Registration completed successfully!");

      // Dispatch event IMMEDIATELY so BrandAuthProvider can start initialization
      window.dispatchEvent(new CustomEvent("brand_token_updated"));

      // Brief delay for event propagation
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Wait for authentication to complete before navigating
      // Poll for brand_user (set by BrandAuthProvider when auth completes)
      const maxWaitTime = 5000; // 5 seconds timeout
      const pollInterval = 100; // Check every 100ms
      const startTime = Date.now();

      const waitForAuth = async (): Promise<void> => {
        return new Promise((resolve) => {
          const checkAuth = () => {
            const elapsed = Date.now() - startTime;

            // Check if brand_user exists in localStorage (set by BrandAuthProvider when auth completes)
            const brandUser = localStorage.getItem("brand_user");
            if (brandUser) {
              try {
                const user = JSON.parse(brandUser);
                // Verify it's a valid user object
                if (user && user.id && user.email) {
                  resolve();
                  return;
                }
              } catch (e) {
                // Invalid JSON, continue polling
              }
            }

            // If timeout reached, resolve anyway (will navigate and SubscriptionGuard will handle redirect if needed)
            if (elapsed >= maxWaitTime) {
              console.warn(
                "Auth initialization timeout reached, proceeding with navigation"
              );
              resolve();
              return;
            }

            // Continue polling
            setTimeout(checkAuth, pollInterval);
          };

          // Start checking immediately
          checkAuth();
        });
      };

      // Wait for auth, then navigate
      await waitForAuth();

      // Fresh session: incomplete setups should land on onboarding again
      clearOnboardingSkip();
      // Navigate to dashboard (SubscriptionGuard will redirect to pricing if no subscription)
      navigate("/brand/dashboard");
      window.location.reload();
    } catch (err: unknown) {
      console.error("Registration error:", err);
      setError(
        (err as any)?.response?.data?.message ||
          "Failed to complete registration. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: string, value: string | boolean | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value as any }));

    if (touched[field]) {
      // Clear specific field error when user starts typing
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const renderError = () =>
    error && (
      <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-lg">{error}</div>
    );

  const renderFieldError = (field: string) => {
    return touched[field] && validationErrors[field] ? (
      <p className="mt-1 text-sm text-red-600">{validationErrors[field]}</p>
    ) : null;
  };

  const filteredNiches =
    nicheQuery === ""
      ? availableNiches
      : availableNiches.filter((niche) =>
          niche.name.toLowerCase().includes(nicheQuery.toLowerCase())
        );

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3].map((step, index) => (
        <div key={step} className="flex items-center">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
              currentStep >= step
                ? "bg-brand-600 text-white"
                : "bg-gray-200 text-muted-foreground"
            }`}
          >
            {currentStep > step ? <Check className="w-5 h-5" /> : step}
          </div>
          {index < 2 && (
            <div
              className={`w-16 h-1 mx-2 transition-all ${
                currentStep > step ? "bg-brand-600" : "bg-gray-200"
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-5">
      <div className="text-center mb-6">
        <h1 className="font-bold text-[24px] leading-[32px] text-foreground">
          Welcome, {formData.firstName || "there"}!
        </h1>
        <p className="mt-2 text-muted-foreground">
          Let's set up your account. Complete these quick steps to get started.
        </p>
      </div>

      {isAdminInvite && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <p className="text-xs font-medium text-emerald-800 mb-1">
            Admin invite detected
          </p>
        </div>
      )}

      {formData.email && (
        <div className="mb-4 p-3 bg-brand-50 border border-brand-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-brand-700 mb-1">
                Registration Email
              </p>
              <p className="text-sm text-brand-900 font-medium">
                {formData.email}
              </p>
            </div>
          </div>
        </div>
      )}

      {formData.website && (
        <div className="mb-4 p-3 bg-brand-50 border border-brand-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-brand-700 mb-1">
                Store URL
              </p>
              <p className="text-sm text-brand-900 font-medium">
                {formData.website}
              </p>
            </div>
          </div>
          <p className="mt-2 text-xs text-brand-600">
            You'll use this store URL to log in next time you visit.
          </p>
        </div>
      )}

      <div>
        <label
          htmlFor="country"
          className="block text-sm font-medium text-muted-foreground"
        >
          Country
        </label>
        <div className="mt-1 relative">
          <select
            id="country"
            value={formData.country}
            onChange={(e) => handleChange("country", e.target.value)}
            className="block w-full px-3 py-2.5 border border-border rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500 bg-card"
          >
            <option value="">Select a country</option>
            {countries.map((country) => (
              <option key={country.code} value={country.name}>
                {country.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Payout methods are deliberately not asked for here. The brand is
          asked how it pays affiliates when the first payout is ready. */}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium text-muted-foreground"
          >
            Password
          </label>
          <div className="relative mt-1">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={(e) => handleChange("password", e.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
              placeholder="Create password"
              className={`block w-full px-3 py-2.5 border ${
                touched.password && validationErrors.password
                  ? "border-red-300"
                  : "border-border"
              } rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm text-muted-foreground hover:text-foreground"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          {renderFieldError("password")}
        </div>

        <div>
          <label
            htmlFor="confirmPassword"
            className="block text-sm font-medium text-muted-foreground"
          >
            Confirm Password
          </label>
          <div className="relative mt-1">
            <input
              id="confirmPassword"
              type={showPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={(e) => handleChange("confirmPassword", e.target.value)}
              onBlur={() =>
                setTouched((prev) => ({ ...prev, confirmPassword: true }))
              }
              placeholder="Confirm password"
              className={`block w-full px-3 py-2.5 border ${
                touched.confirmPassword && validationErrors.confirmPassword
                  ? "border-red-300"
                  : "border-border"
              } rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500`}
            />
          </div>
          {renderFieldError("confirmPassword")}
        </div>
      </div>

      <div className="flex items-start pt-2">
        <div className="flex items-center h-5">
          <input
            id="agreeToTerms"
            type="checkbox"
            checked={formData.agreeToTerms}
            onChange={(e) => handleChange("agreeToTerms", e.target.checked)}
            className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-border rounded"
          />
        </div>
        <div className="ml-3 text-sm">
          <label htmlFor="agreeToTerms" className="font-medium text-muted-foreground">
            I agree to the{" "}
            <Link
              to="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:text-brand-500"
            >
              Terms and Conditions
            </Link>
          </label>
          {renderFieldError("agreeToTerms")}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-5">
      <div className="text-center mb-6">
        <h1 className="font-bold text-[24px] leading-[32px] text-foreground">
          Your Brand Profile
        </h1>
        <p className="mt-2 text-muted-foreground">
          Tell us about your brand and select your niches.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-muted-foreground mb-2">
          Brand Logo
        </label>
        <ImageUploader
          id="signup-company-logo"
          image={logoPreviewUrl}
          onChange={handleLogoUpload}
          size="md"
          shape="round"
          layout="horizontal"
          helpText={
            isUploadingLogo ? "Uploading..." : "PNG, JPG or WebP (max. 3MB)"
          }
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-muted-foreground">
          Select Your Niches
        </label>
        <div className="relative mt-1">
          <div
            className={`flex flex-wrap gap-2 p-3 border ${
              touched.niches && validationErrors.niches
                ? "border-red-300"
                : "border-border"
            } rounded-lg min-h-[44px] cursor-pointer bg-card`}
            onClick={() => {
              setIsNicheOpen(!isNicheOpen);
              setTouched((prev) => ({ ...prev, niches: true }));
            }}
          >
            {formData.niches.map((nicheId) => {
              const niche = availableNiches.find((n) => n.id === nicheId);
              return niche ? (
                <span
                  key={`selected-${nicheId}`}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-brand-50 text-brand-700 rounded-md text-sm"
                  onClick={(e) => e.stopPropagation()}
                >
                  {niche.name}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const updatedNiches = formData.niches.filter(
                        (id) => id !== nicheId
                      );
                      handleChange("niches", updatedNiches);
                    }}
                    className="hover:text-brand-900"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </span>
              ) : null;
            })}
            {formData.niches.length === 0 && (
              <span className="text-muted-foreground">Select niches...</span>
            )}
            <ChevronDown
              className={`h-5 w-5 text-muted-foreground ml-auto transition-transform ${
                isNicheOpen ? "rotate-180" : ""
              }`}
            />
          </div>
          {renderFieldError("niches")}

          {isNicheOpen && (
            <div className="absolute z-10 mt-1 w-full bg-card border border-border rounded-lg shadow-lg py-1 max-h-60 overflow-auto">
              <input
                type="text"
                className="w-full px-3 py-2 border-b border-border focus:outline-none focus:ring-0"
                placeholder="Search niches..."
                value={nicheQuery}
                onChange={(e) => setNicheQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
              {isLoadingNiches ? (
                <div className="px-3 py-2 text-muted-foreground">Loading niches...</div>
              ) : filteredNiches.length === 0 ? (
                <div className="px-3 py-2 text-muted-foreground">No niches found</div>
              ) : (
                filteredNiches.map((niche) => (
                  <button
                    key={`option-${niche.id}`}
                    type="button"
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center justify-between ${
                      formData.niches.includes(niche.id)
                        ? "text-brand-700 bg-brand-50"
                        : "text-foreground"
                    }`}
                    onClick={() => {
                      const updatedNiches = formData.niches.includes(niche.id)
                        ? formData.niches.filter((id) => id !== niche.id)
                        : [...formData.niches, niche.id];
                      handleChange("niches", updatedNiches);
                    }}
                    disabled={
                      formData.niches.length >= 3 &&
                      !formData.niches.includes(niche.id)
                    }
                  >
                    {niche.name}
                    {formData.niches.includes(niche.id) && (
                      <span className="text-brand-600">✓</span>
                    )}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Select up to 3 industries that describe your brand the best
        </p>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-5">
      <div className="text-center mb-6">
        <h1 className="font-bold text-[24px] leading-[32px] text-foreground">
          Program Settings
        </h1>
        <p className="mt-2 text-muted-foreground">
          Configure your default affiliate program settings.
        </p>
      </div>

      <div>
        <label
          htmlFor="commissionRate"
          className="block text-sm font-medium text-muted-foreground"
        >
          Default Commission Rate (%)
        </label>
        <div className="relative mt-1">
          <input
            id="commissionRate"
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={programSettings.commissionRate}
            onChange={(e) =>
              setProgramSettings((prev) => ({
                ...prev,
                commissionRate: e.target.value,
              }))
            }
            placeholder="10"
            className="block w-full px-3 py-2.5 border border-border rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
          />
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <span className="text-muted-foreground">%</span>
          </div>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          The percentage commission affiliates earn on each sale
        </p>
      </div>

      <div>
        <label
          htmlFor="discountPercentage"
          className="block text-sm font-medium text-muted-foreground"
        >
          Default Discount Percentage (%)
        </label>
        <div className="relative mt-1">
          <input
            id="discountPercentage"
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={programSettings.discountPercentage}
            onChange={(e) =>
              setProgramSettings((prev) => ({
                ...prev,
                discountPercentage: e.target.value,
              }))
            }
            placeholder="10"
            className="block w-full px-3 py-2.5 border border-border rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
          />
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <span className="text-muted-foreground">%</span>
          </div>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          The discount customers get when using affiliate codes
        </p>
      </div>

      <div className="flex items-start pt-2 p-4 bg-muted rounded-lg border border-border">
        <div className="flex items-center h-5">
          <input
            id="autoApproveAffiliates"
            type="checkbox"
            checked={programSettings.autoApproveAffiliates}
            onChange={(e) =>
              setProgramSettings((prev) => ({
                ...prev,
                autoApproveAffiliates: e.target.checked,
              }))
            }
            className="h-5 w-5 text-brand-600 focus:ring-brand-500 border-border rounded"
          />
        </div>
        <div className="ml-3">
          <label
            htmlFor="autoApproveAffiliates"
            className="font-medium text-foreground"
          >
            Auto-approve new affiliates
          </label>
          <p className="text-sm text-muted-foreground mt-0.5">
            Automatically approve new affiliates when they join your program
          </p>
        </div>
      </div>

      {/* Attribution Method Selection */}
      <div className="border-t pt-5">
        <AttributionMethodSelector
          selectedMethod={
            programSettings.attributionMethod as AttributionMethod
          }
          onMethodChange={(method) =>
            setProgramSettings((prev) => ({
              ...prev,
              attributionMethod: method,
            }))
          }
        />
      </div>
    </div>
  );

  return (
    <div className="md:w-[480px] md:m-auto pt-4 mx-auto h-[88%] md:h-screen">
      <div className="m-auto w-[98%] h-[96%] overflow-y-auto flex items-center justify-center py-6 pb-4 bg-card border border-border rounded-[24px] shadow-[1px_5px_10px_0px_#D1D1D11A,4px_18px_19px_0px_#D1D1D117,9px_41px_25px_0px_#D1D1D10D,15px_73px_30px_0px_#D1D1D103,24px_115px_33px_0px_#D1D1D100]">
        <div className="w-full px-6 h-full flex flex-col">
          <div className="flex-1">
            {renderStepIndicator()}
            {renderError()}

            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
          </div>

          <div className="mt-6 space-y-3">
            {currentStep < 3 ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={isLoading}
                className="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50"
              >
                Next
              </button>
            ) : (
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => handleSubmit(false)}
                  disabled={isLoading}
                  className="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-t-2 border-white rounded-full animate-spin mr-2" />
                  ) : null}
                  <span>Complete Registration</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleSubmit(true)}
                  disabled={isLoading}
                  className="w-full flex justify-center items-center py-2.5 px-4 border border-border rounded-lg shadow-sm text-sm font-medium text-muted-foreground bg-card hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50"
                >
                  Skip for now
                </button>
              </div>
            )}

            {currentStep > 1 && (
              <button
                type="button"
                onClick={() => setCurrentStep((currentStep - 1) as Step)}
                disabled={isLoading}
                className="w-full flex justify-center items-center py-2.5 px-4 border border-border rounded-lg shadow-sm text-sm font-medium text-muted-foreground bg-card hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50"
              >
                Back
              </button>
            )}

            <div className="text-center border border-brand-600 shadow-[0px_1px_2px_0px_#0A0D120D] rounded-lg p-2">
              <span className="font-bold text-[14px] leading-[20px] tracking-normal text-muted-foreground">
                Already have an account?{" "}
              </span>
              <Link
                to="/auth/login"
                className="text-sm text-brand-600 font-bold text-[14px] tracking-normal hover:text-purple pl-1"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
